import { Select, Spin, Tag } from 'antd'
import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useDebounce } from 'react-use'

import type { SizeType } from 'antd/es/config-provider/SizeContext'
import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { Label } from 'common/components/Label/Label'
import type { EmrCodeItem } from 'features/Procedure/state/procedure-api.type'
import { useLazyFetchEmrCodesQuery } from 'features/Procedure/state/procedure.api'
import {
  ProcedureTypeCode,
  type ProcedureTypeCode as ProcedureTypeCodeType,
} from 'features/Procedure/state/procedure.type'
import styles from './code.module.scss'

export type EmrCodeOption = {
  label: string
  value: string
}

type Props = {
  onChange?: (value: EmrCodeItem[] | EmrCodeItem | null) => void
  value?: EmrCodeItem[] | EmrCodeItem | null
  placeholder?: string
  codeType: ProcedureTypeCodeType
  debounceTimeout?: number
  minSearchLength?: number
  mode?: 'multiple' | 'tags' | undefined
  disabled?: boolean
  className?: string
  error?: string
  label?: string
  required?: boolean
  size?: SizeType
  category?: string
}

const adjustValue = (item: EmrCodeItem, forDisplay = false) => {
  // For ICD10 codes, format as "ICD10|code"
  const codeText = item.code_text.replace(/<\/?[^>]+(>|$)/g, '')
  if (item.code_type === ProcedureTypeCode.ICD10) {
    const value = `${item.code_type}|${item.code.replace(/\s+/g, '')}`
    return {
      label: forDisplay ? value : codeText ? `${value}|${codeText}` : value,
      value: value,
    }
  }

  // For other codes, use the original format
  const value = `${item.code.replace(/\s+/g, '')} ${codeText}`
  return {
    label: value,
    value: value,
  }
}

export const CodeSelect = ({
  value,
  onChange,
  placeholder = 'Search codes',
  codeType,
  debounceTimeout = 500,
  minSearchLength = 1,
  mode,
  disabled = false,
  className,
  error,
  label,
  required = false,
  size = 'middle',
  category,
}: Props) => {
  const [searchValue, setSearchValue] = useState('')
  const { siteId } = useParams<{ siteId: string }>()

  const [fetchEmrCodes, { isFetching, data }] = useLazyFetchEmrCodesQuery()

  useDebounce(
    () => {
      if (!searchValue || !siteId || searchValue.length < minSearchLength) {
        return
      }

      fetchEmrCodes({
        siteId,
        search: searchValue,
        code_type: codeType,
        ...(category && { category }),
      })
    },
    debounceTimeout,
    [searchValue, category],
  )

  const handleSearch = useCallback((search: string) => {
    setSearchValue(search)
  }, [])

  const options = useMemo(() => {
    if (!data) return []
    return data.map((item) => {
      return {
        ...adjustValue(item, false), // Include codeText for dropdown options
        item,
      }
    })
  }, [data])

  const selectValue = useMemo(() => {
    if (!value) return value
    if (Array.isArray(value)) {
      return value.map((item) => ({
        ...adjustValue(item, true), // Clean display for selected values
      }))
    }
    return adjustValue(value, true) // Clean display for selected values
  }, [value])

  const handleChange = useCallback(
    (newValue: any) => {
      if (!onChange) return

      if (!newValue) {
        onChange(null)
        return
      }

      if (Array.isArray(newValue)) {
        const selectedItems = newValue
          .map((v) => {
            const option = options.find((opt) => opt.value === v.value)
            if (option) return option.item

            if (value && Array.isArray(value)) {
              const existingItem = value.find((item) => {
                const adjusted = adjustValue(item)
                return adjusted.value === v.value
              })
              return existingItem
            }
            return null
          })
          .filter(Boolean) as EmrCodeItem[]
        onChange(selectedItems)
      } else {
        const option = options.find((opt) => opt.value === newValue.value)
        if (option) {
          onChange(option.item)
        } else if (value && !Array.isArray(value)) {
          const adjusted = adjustValue(value)
          if (adjusted.value === newValue.value) {
            onChange(value)
          }
        }
      }
    },
    [onChange, options, value],
  )

  const tagRender = useCallback(
    (props: any) => {
      const { label, value: tagValue, closable, onClose } = props
      if (codeType === ProcedureTypeCode.ICD10 && mode === 'multiple') {
        return (
          <Tag closable={closable} onClose={onClose} className={styles.tag} bordered={false}>
            {tagValue}
          </Tag>
        )
      }
      return (
        <Tag closable={closable} onClose={onClose} className={styles.tag}>
          {label}
        </Tag>
      )
    },
    [codeType, mode],
  )

  return (
    <div className={classNames(styles.wrapper, className)}>
      <Label label={label} required={required} />
      <Select
        size={size}
        mode={mode}
        allowClear
        labelInValue
        value={selectValue}
        placeholder={placeholder}
        filterOption={false}
        onSearch={handleSearch}
        onChange={handleChange}
        notFoundContent={isFetching ? <Spin size='small' /> : 'No results found'}
        options={options}
        disabled={disabled}
        className={styles.select}
        popupClassName={styles.dropdown}
        status={error ? 'error' : undefined}
        showSearch
        tagRender={mode === 'multiple' ? tagRender : undefined}
      />
      <ErrorLabel error={error} />
    </div>
  )
}
